import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowClockwiseIcon, CameraIcon, ImagesIcon, PlayCircleIcon, PlusIcon, StarIcon, UploadSimpleIcon, VideoIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { MEDIA_FILTERS, mediaSourceLabel, mediaSourceTone, validatePhotoFile, youtubeEmbedUrl } from "@/lib/mediaGallery";
import { useAuth } from "@/context/AuthContext";
import { isAdminUser } from "@/lib/admin";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

export function CommunityMediaGallery({ destinationId, trailId = null, title = "Media", onCoverChanged, compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, requestMediaContribution } = useAuth();
  const [filter, setFilter] = useState("ALL");
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [contributionMode, setContributionMode] = useState(null);
  const resumedAction = useRef(false);
  const [settingCoverId, setSettingCoverId] = useState(null);
  const canSetCover = !trailId && isAdminUser(user);

  const loadGallery = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get("/media", {
        params: { destination_id: destinationId, trail_id: trailId, filter },
      });
      setGallery(data);
    } catch (error) {
      setLoadError(formatApiErrorDetail(error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [destinationId, filter, trailId]);

  useEffect(() => { loadGallery(); }, [loadGallery]);

  useEffect(() => {
    if (!user || resumedAction.current) return;
    const params = new URLSearchParams(location.search);
    const mode = params.get("media");
    if (!["photo", "video"].includes(mode)) return;
    resumedAction.current = true;
    setContributionMode(mode);
    params.delete("media");
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ""}#community-media`, { replace: true });
  }, [location.pathname, location.search, navigate, user]);

  const openContribution = (mode) => {
    const result = requestMediaContribution({ destinationId, trailId, mode });
    if (result.needAuth) {
      toast(translateLabel("Sign in to contribute media"));
      navigate("/login");
      return;
    }
    setContributionMode(mode);
  };

  const previewItems = useMemo(
    () => (gallery?.items || []).filter((item) => item.id !== gallery?.hero?.id).slice(0, 4),
    [gallery],
  );

  const setAsCover = async (media) => {
    setSettingCoverId(media.id);
    try {
      const { data } = await api.post("/media/cover", { media_id: media.id });
      toast.success(translateLabel("Destination cover updated"));
      onCoverChanged?.(data);
      await loadGallery();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || error.message));
    } finally {
      setSettingCoverId(null);
    }
  };

  return (
    <section id="community-media" data-detail-section="media" className={`${compact ? "mb-8" : "mb-12"} scroll-mt-24`}>
      <div className={`${compact ? "mb-3 items-center" : "mb-4 items-end"} flex flex-wrap justify-between gap-3`}>
        <div>
          <h2 className={`font-display font-bold uppercase text-white ${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"}`}>{title}</h2>
          <p className={`mt-1 text-zinc-400 ${compact ? "text-xs" : "text-sm"}`}>{translateLabel("Official and rider-contributed photos and YouTube videos.")}</p>
        </div>
        <div className={compact ? "inline-flex overflow-hidden rounded-full border border-white/10 bg-white/5" : "flex flex-wrap gap-2"}>
          <button
            type="button"
            onClick={() => openContribution("photo")}
            className={compact ? "inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white hover:bg-white/10" : "media-action"}
            data-testid="add-media-photo"
          >
            <CameraIcon size={16} weight="bold" /> {translateLabel("Add photo")}
          </button>
          <button
            type="button"
            onClick={() => openContribution("video")}
            className={compact ? "inline-flex items-center gap-1.5 border-l border-white/10 px-3 py-2 text-xs font-bold text-white hover:bg-white/10" : "media-action"}
            data-testid="add-media-video"
          >
            <VideoIcon size={16} weight="bold" /> {translateLabel(compact ? "Add video" : "Add YouTube video")}
          </button>
        </div>
      </div>

      {(gallery?.total > 0 || filter !== "ALL") && (
        <div className="mb-4 flex gap-2" role="group" aria-label={translateLabel("Media filter")}>
          {MEDIA_FILTERS.map((option) => (
            <button type="button" key={option.value} onClick={() => setFilter(option.value)} aria-pressed={filter === option.value} data-testid={`media-filter-${option.value.toLowerCase()}`} className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${filter === option.value ? "bg-[#CCFF00] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
              {translateLabel(option.label)}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        compact ? (
          <div className="grid grid-cols-2 gap-2" data-testid="media-loading">
            <Skeleton className="col-span-2 h-28 rounded-2xl bg-white/5" />
            <Skeleton className="h-16 rounded-xl bg-white/5" />
            <Skeleton className="h-16 rounded-xl bg-white/5" />
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2" data-testid="media-loading"><Skeleton className="h-72 rounded-2xl bg-white/5 md:col-span-2" /><Skeleton className="h-40 rounded-2xl bg-white/5" /><Skeleton className="h-40 rounded-2xl bg-white/5" /></div>
        )
      ) : loadError ? (
        <div className={`rounded-2xl border border-red-500/30 bg-red-500/5 text-center ${compact ? "p-4" : "p-6"}`} data-testid="media-error">
          <p className="text-sm font-semibold text-red-200">{loadError}</p>
          <button type="button" onClick={loadGallery} className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-black"><ArrowClockwiseIcon size={15} weight="bold" /> {translateLabel("Retry")}</button>
        </div>
      ) : gallery?.items?.length ? (
        <div data-testid="media-gallery">
          {gallery.hero && <MediaHero media={gallery.hero} canSetCover={canSetCover} settingCoverId={settingCoverId} onSetCover={setAsCover} />}
          {previewItems.length > 0 && <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{previewItems.map((media) => <MediaTile key={media.id} media={media} canSetCover={canSetCover} settingCoverId={settingCoverId} onSetCover={setAsCover} />)}</div>}
          <button type="button" onClick={() => setViewAllOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10" data-testid="view-all-community-media"><ImagesIcon size={17} /> {translateLabel("View all ({count})", { count: gallery.total })}</button>
        </div>
      ) : (
        compact ? (
          <div className="flex items-center gap-3 rounded-2xl border border-dashed border-white/10 bg-[#121214] p-4 text-left" data-testid="media-empty">
            <ImagesIcon size={24} className="shrink-0 text-[#CCFF00]" />
            <div><p className="text-sm font-semibold text-zinc-200">{translateLabel("No {type} yet.", { type: translateLabel(filter === "PHOTOS" ? "photos" : filter === "VIDEOS" ? "videos" : "media") })}</p><p className="mt-1 text-xs text-zinc-500">{translateLabel("Be the first rider to contribute.")}</p></div>
          </div>
        ) : (
          <div className="flex min-h-40 items-center justify-center rounded-2xl border border-dashed border-white/10 bg-[#121214] p-5 text-center" data-testid="media-empty">
            <div><ImagesIcon size={30} className="mx-auto text-[#CCFF00]" /><p className="mt-3 text-sm font-semibold text-zinc-200">No {filter === "PHOTOS" ? "photos" : filter === "VIDEOS" ? "videos" : "media"} yet.</p><p className="mt-1 text-xs text-zinc-500">{translateLabel("Be the first rider to contribute.")}</p></div>
          </div>
        )
      )}

      <Dialog open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-y-auto border-white/10 bg-[#121214] text-white">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{translateLabel("All media")}</DialogTitle><DialogDescription>{translateLabel("{count} public items in the selected filter.", { count: gallery?.total || 0 })}</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{(gallery?.items || []).map((media) => <MediaTile key={media.id} media={media} expanded canSetCover={canSetCover} settingCoverId={settingCoverId} onSetCover={setAsCover} />)}</div>
        </DialogContent>
      </Dialog>

      <ContributionDialog mode={contributionMode} onOpenChange={(open) => { if (!open) setContributionMode(null); }} destinationId={destinationId} trailId={trailId} onComplete={async () => { setContributionMode(null); setFilter("ALL"); await loadGallery(); }} />
    </section>
  );
}

function SourceBadge({ media }) {
  const official = mediaSourceTone(media.source_type) === "official";
  return <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${official ? "bg-[#CCFF00] text-black" : "bg-black/70 text-white"}`}>{mediaSourceLabel(media.source_type)}</span>;
}

function CoverAction({ media, canSetCover, settingCoverId, onSetCover }) {
  if (!canSetCover || media.media_type !== "PHOTO") return null;
  if (media.is_cover) {
    return <span className="rounded-full bg-[#CCFF00] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-black"><StarIcon size={12} weight="fill" className="mr-1 inline" /> {translateLabel("Cover")}</span>;
  }
  return (
    <button
      type="button"
      onClick={() => onSetCover(media)}
      disabled={settingCoverId === media.id}
      className="rounded-full border border-white/15 bg-black/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-white backdrop-blur hover:bg-black disabled:opacity-50"
      data-testid={`set-cover-${media.id}`}
    >
      <StarIcon size={12} className="mr-1 inline" /> {translateLabel(settingCoverId === media.id ? "Setting…" : "Set as cover")}
    </button>
  );
}

function MediaHero({ media, canSetCover, settingCoverId, onSetCover }) {
  const embed = youtubeEmbedUrl(media);
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="media-hero">
      {media.media_type === "PHOTO" ? <img src={media.url} alt={media.title || translateLabel("Gallery hero")} className="h-[360px] w-full object-cover md:h-[480px]" /> : embed ? <iframe src={embed} title={media.title || translateLabel("Gallery video")} className="aspect-video w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /> : <a href={media.url} target="_blank" rel="noreferrer" className="flex h-72 items-center justify-center text-[#CCFF00]"><PlayCircleIcon size={64} /></a>}
      <div className="absolute left-3 top-3"><SourceBadge media={media} /></div>
      <div className="absolute right-3 top-3"><CoverAction media={media} canSetCover={canSetCover} settingCoverId={settingCoverId} onSetCover={onSetCover} /></div>
    </div>
  );
}

function MediaTile({ media, expanded = false, canSetCover, settingCoverId, onSetCover }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black">
      {media.media_type === "PHOTO" ? <img src={media.url} alt={media.title || translateLabel("Rider media")} className={`w-full object-cover ${expanded ? "h-56" : "h-40"}`} loading="lazy" /> : <a href={media.url} target="_blank" rel="noreferrer" className={`flex w-full items-center justify-center bg-zinc-950 text-[#CCFF00] ${expanded ? "h-56" : "h-40"}`}><PlayCircleIcon size={48} weight="fill" /><span className="sr-only">{translateLabel("Open YouTube video")}</span></a>}
      <div className="absolute left-2 top-2"><SourceBadge media={media} /></div>
      <div className="absolute right-2 top-2"><CoverAction media={media} canSetCover={canSetCover} settingCoverId={settingCoverId} onSetCover={onSetCover} /></div>
      {media.title && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-3 pt-8 text-xs font-semibold text-white">{media.title}</div>}
    </div>
  );
}

function ContributionDialog({ mode, onOpenChange, destinationId, trailId, onComplete }) {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode) return;
    setFile(null); setUrl(""); setTitle(""); setProgress(0); setError("");
  }, [mode]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (mode === "photo") { const validation = validatePhotoFile(file); if (validation) { setError(validation); return; } }
    setSubmitting(true);
    try {
      if (mode === "photo") {
        await api.uploadMediaPhoto({ destinationId, trailId, file, onProgress: setProgress });
        toast.success(translateLabel("Photo uploaded"));
      } else {
        await api.post("/media/videos", { destination_id: destinationId, trail_id: trailId, url, title });
        toast.success(translateLabel("Video added"));
      }
      await onComplete();
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail || requestError.message));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={Boolean(mode)} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#121214] text-white">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{translateLabel(mode === "video" ? "Add YouTube video" : "Upload photo")}</DialogTitle><DialogDescription>{translateLabel(mode === "video" ? "Paste a supported YouTube link. Direct video uploads are not supported." : "JPEG, PNG or WebP, up to 15 MB.")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {mode === "photo" ? (
            <label className="block cursor-pointer rounded-xl border border-dashed border-white/15 bg-white/5 p-5 text-center hover:bg-white/10"><UploadSimpleIcon size={26} className="mx-auto text-[#CCFF00]" /><span className="mt-2 block text-sm font-semibold text-white">{file?.name || translateLabel("Choose photo")}</span><input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(event) => setFile(event.target.files?.[0] || null)} /></label>
          ) : (
            <><label className="block text-xs font-bold uppercase tracking-wide text-zinc-400">{translateLabel("YouTube URL")}<input value={url} onChange={(event) => setUrl(event.target.value)} type="url" required placeholder="https://youtu.be/..." className="mt-2 h-11 w-full rounded-xl border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-[#CCFF00]" /></label><label className="block text-xs font-bold uppercase tracking-wide text-zinc-400">{translateLabel("Title (optional)")}<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} className="mt-2 h-11 w-full rounded-xl border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-[#CCFF00]" /></label></>
          )}
          {submitting && mode === "photo" && <div><div className="mb-1 flex justify-between text-xs text-zinc-400"><span>{translateLabel("Uploading")}</span><span>{progress}%</span></div><div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-[#CCFF00] transition-all" style={{ width: `${progress}%` }} /></div></div>}
          {error && <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-200" role="alert">{error} {translateLabel("You can retry without creating a gallery item.")}</div>}
          <button type="submit" disabled={submitting} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#CCFF00] px-5 py-3 text-sm font-bold text-black disabled:opacity-60"><PlusIcon size={16} weight="bold" /> {translateLabel(submitting ? "Working…" : mode === "video" ? "Add video" : error ? "Retry upload" : "Upload photo")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
